import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { getAll, getOne, StateDictionary } from '@appState/helpers';
import { CommunityBase, RecordingsCommunityApproveAutomaticallyRequest } from '@models/recordings/recordings-communities.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { RecordingsCommunitiesService } from './recordings-communities.service';

type RecordingsCommunitiesState = {
  recordingsCommunities: CommunityBase[];
  isLoading: boolean;
  selectedRecordingsCommunity: CommunityBase | null;
  isSelectedRecordingsCommunityLoading: boolean;
};

const initialState: RecordingsCommunitiesState = {
  recordingsCommunities: [],
  isLoading: false,
  selectedRecordingsCommunity: null,
  isSelectedRecordingsCommunityLoading: false,
};

const recordingsCommunitiesDictionary: StateDictionary<CommunityBase> = {
  entityList: 'recordingsCommunities',
  listLoading: 'isLoading',
  selectedEntity: 'selectedRecordingsCommunity',
  selectedEntityLoading: 'isSelectedRecordingsCommunityLoading',
  entityId: 'id',
  createLoading: '',
  sortFn: (a, b) => a.name.localeCompare(b.name),
};
export const RecordingsCommunitiesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('recordings-communities-store'),

  withComputed(store => ({
    selectedRecordingsCommunityId: computed(() => store.selectedRecordingsCommunity()?.id || null),
  })),

  withMethods((store, recordingsCommunitiesService = inject(RecordingsCommunitiesService)) => {
    const repositoryMethods = {
      getAll: getAll<CommunityBase>({
        store,
        service: recordingsCommunitiesService,
        dictionary: recordingsCommunitiesDictionary,
      }),
      getOne: getOne<CommunityBase>({
        store,
        service: recordingsCommunitiesService,
        dictionary: recordingsCommunitiesDictionary,
        onErrorCallback() {
          patchState(store, { selectedRecordingsCommunity: null });
        },
      }),
      toggleAutomaticApproval: rxMethod<{ id: string; req: RecordingsCommunityApproveAutomaticallyRequest }>(
        pipe(
          tap(() => patchState(store, { isSelectedRecordingsCommunityLoading: true })),
          switchMap(({ id, req }) =>
            recordingsCommunitiesService.toggleAutomaticApproval(id, req).pipe(
              tap(recordingsCommunity => {
                patchState(store, {
                  selectedRecordingsCommunity: recordingsCommunity,
                  isSelectedRecordingsCommunityLoading: false,
                  recordingsCommunities: store
                    .recordingsCommunities()
                    .map(item => (item.id === recordingsCommunity.id ? recordingsCommunity : item)),
                });
              }),
              catchError(() => {
                patchState(store, { isSelectedRecordingsCommunityLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };
    const selectRecordingsCommunity = (id: string) => {
      const selected = store.recordingsCommunities().find(at => at.id === id) || null;
      patchState(store, { selectedRecordingsCommunity: selected });
      if (!selected) repositoryMethods.getOne(id);
    };

    const resetState = (state: Partial<RecordingsCommunitiesState> = {}) => {
      patchState(store, { ...initialState, ...state });
    };
    return {
      ...repositoryMethods,
      selectRecordingsCommunity,
      resetState,
    };
  }),
);
export interface RecordingsCommunitiesStoreType {
  readonly recordingsCommunities: Signal<CommunityBase[]>;
  readonly isLoading: Signal<boolean>;
  readonly isSelectedRecordingsCommunityLoading: Signal<boolean>;
  readonly selectedRecordingsCommunity: Signal<CommunityBase | null>;

  readonly selectedRecordingsCommunityId: Signal<string | null>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly toggleAutomaticApproval: (input: { id: string; req: RecordingsCommunityApproveAutomaticallyRequest }) => void;
  readonly selectRecordingsCommunity: (id: string) => void;
}
